import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import convertUnit from '../../Services/conversions';
import { map } from '../../Services/utilities';
import { translate } from '../../Services/translation';
import { numberFormat } from '../../Services/formatting';
import { setTotalWeight as setWeight, setUnit, setBaseUnitQuantity, setSalesQuantity, } from '../../Actions/Configurator.action';
import { googleAnalytics } from '../../Services/googleAnalytics';

const ProductQuantitySelector = () => {
  const dispatch = useDispatch();
  const articleNumber = useSelector((state) => state.configurator.id);
  const configurator = useSelector((state) => state.configurator);
  const { totalWeight, orderRow, rules, selections, quantitySelector, baseUnitQuantity, informationToCustomer, infoTextQuantityMinLimit, selectedTab, } = configurator;

  if (!quantitySelector) {
    return null;
  }

  const { decimalQuantity, defaultUnit, baseUnit, unitConfigurableParentItem, quantityUnits, } = quantitySelector;

  const isUpdateMode = orderRow ? true : false,
    isConfigured = selectedTab !== 1,
    [selectedUnit, setSelectedUnit] = useState(quantitySelector.selectedUnit),
    [selectedUnitQuantity, setSelectedUnitQuantity] = useState(configurator.baseUnitQuantity),
    [baseQuantity, setBaseQuantity] = useState(configurator.baseUnitQuantity);

  const cuttingLength = selections['cuttingLength'] ? selections['cuttingLength'] : -1;

  const quantityInputElement = useRef(null);
  const decimalQuantityMin = 1;
  const readOnly = rules.isRemnant;

  useEffect(() => {
    initialize();
  }, [baseUnit, defaultUnit, isUpdateMode, rules.isRemnant, articleNumber, unitConfigurableParentItem, isConfigured, cuttingLength, quantityUnits,]);

  const initialize = () => {
    onSetUnit(selectedUnit.id);

    if (isUpdateMode) {
      updateQuantity(convertUnit(baseQuantity, baseUnit, selectedUnit), selectedUnit);
    } else {
      const initialQuantity = baseUnit.conversionFactor > 0 ? baseUnit.conversionFactor : 1;
      updateQuantity(initialQuantity, defaultUnit);
    }

    if (readOnly) {
      handleChangeQuantityUnit('pcs');
    }
  };

  const onSetUnit = (unit) => {
    dispatch(setUnit(unit));
  };

  const onTotalWeightChange = (quantity) => {
    dispatch(setWeight(quantity));
  };

  const onBaseUnitQuantityChange = (baseQuantity) => {
    dispatch(setBaseUnitQuantity(baseQuantity));
  };

  const updateQuantity = (quantity, unit, isUnitSwitch = false) => {
    let baseQuantity = convertUnit(quantity, unit, baseUnit);

    if (!decimalQuantity) {
      if (isUnitSwitch) {
        baseQuantity = Math.round(baseQuantity.toFixed(2));

      } else {
        baseQuantity = Math.ceil(baseQuantity.toFixed(2));
      }
    }

    setBaseQuantity(baseQuantity);
    onBaseUnitQuantityChange(baseQuantity);

    let totalWeight = baseQuantity * convertUnit(1, baseUnit, getQuantityUnit('kg')).toFixed(2);
    setTotalWeight(totalWeight);

    setSelectedUnitQuantityState(baseQuantity * convertUnit(1, baseUnit, unit).toFixed(2));
  };

  const setTotalWeight = (weight) => {
    if (isCutLengthValid()) {
      weight = map(cuttingLength, 0, rules.length, 0, weight);
    } else if (rules.isRemnant && defaultUnit.unit === 'm') {
      weight = (rules.length / 1000) * weight;
    }
    onTotalWeightChange(weight);
  };

  const isCutLengthValid = () => {
    return (cuttingLength >= rules.minCutLength && cuttingLength <= rules.maxCutLength);
  };

  const getQuantityUnit = (unit) => {
    return quantityUnits.find((u) => u.unit === unit);
  };

  const setSelectedUnitQuantityState = (quantity) => {
    const fixedValue = Number(parseFloat(quantity).toFixed(2));
    setSelectedUnitQuantity(fixedValue);

    dispatch(setSalesQuantity(fixedValue));

    if (quantityInputElement.current) {
      quantityInputElement.current.value = fixedValue;
    }
  };

  const onQuantityInputElementPress = (event, isBlur) => {
    let quantity = parseFloat(event.target.value);
  
    if (event.key === 'Enter' || isBlur) {
      if (quantity !== selectedUnitQuantity) {
        if (!quantity || quantity < decimalQuantityMin) {
          quantity = decimalQuantityMin;
        }
  
        updateQuantity(quantity, selectedUnit);
  
        if (isBlur) {
          googleAnalytics({
            'event': 'productConfigurator',
            'fieldName': 'Quantity',
            'value': quantity.toString(),
            'unit': selectedUnit.unit,
            'productId': articleNumber,
          });
        }
      }
    }
  };
  

  const handleChangeQuantityUnit = (unit) => {
    const quantityUnit = getQuantityUnit(unit);

    if (quantityUnit) {
      setSelectedUnit(quantityUnit);
      onSetUnit(quantityUnit.id);

      if (quantityInputElement.current) {
        const newQuantity = convertUnit(selectedUnitQuantity, selectedUnit, quantityUnit);
        updateQuantity(newQuantity, quantityUnit, true);
      }
    }
  };

  let totalPcsWeight = convertUnit(baseUnitQuantity, baseUnit, getQuantityUnit('pcs'));
  let showInfoText = infoTextQuantityMinLimit == 0 ? true : totalPcsWeight >= infoTextQuantityMinLimit;

  return (
    <Fragment>
      {informationToCustomer && showInfoText && (
        <div className='configurator__information'>
          <svg>
            <use href='/ui/images/sprites/be-group.svg#attention-i'></use>
          </svg>
          <span>{informationToCustomer}</span>
        </div>
      )}
      <section className='configurator-box'>
        <header>
          <svg className='configurator-box__icon'>
            <use href='/ui/images/sprites/be-group.svg#quantity'></use>
          </svg>
          <h4 className='configurator-box__title'>
            {translate('config.productservicequandity')}
          </h4>
          <span>
            {translate('cart.weight')}: {numberFormat.format(totalWeight)} kg
          </span>
        </header>
        <div className='configurator-box__content'>
          <div className='select-quantity'>
            <div className='quantity'>
              <input type='number' disabled={readOnly} defaultValue={selectedUnitQuantity.toString()} onInput={(event) => onQuantityInputElementPress(event, false)}
                onBlur={(event) => onQuantityInputElementPress(event, true)} ref={quantityInputElement} data-buy-quantity-input min='0' step='1' maxLength='9'
                className='product-detail__quantity-input form__input'
              />
            </div>
            <div className='quantity-unit'>
              {quantityUnits && quantityUnits.length ? (
                <select className='form' disabled={isConfigured || readOnly} value={selectedUnit.unit}
                  onChange={(event) => { handleChangeQuantityUnit(event.target.value); }}                >
                  {quantityUnits.filter((unit) => unit.available || isConfigured || readOnly)
                    .map((quantityUnit) => (
                      <option value={quantityUnit.unit} key={quantityUnit.unit}>
                        {quantityUnit.description}
                      </option>
                    ))}
                </select>
              ) : (
                <label htmlFor={configurator.quantityFieldId} className='product__unit'>
                  {baseUnit.description}
                </label>
              )}
            </div>
          </div>
          <p className='weight-text'>
            {translate('config.weight.information')}
          </p>
        </div>
      </section>
    </Fragment>
  );
};

export default ProductQuantitySelector;
